"use client";

import Image from "next/image";
import { useState, useCallback, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Eye } from "lucide-react";

const certificates = [
  { id: 1, src: "/aboutus/certificate1.jpg" },
  { id: 2, src: "/aboutus/certificate2.jpg" },
  { id: 3, src: "/aboutus/certificate3.png" },
  { id: 4, src: "/aboutus/certificate4.png" },
];

export default function Certifications() {
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) =>
      prev === 0 ? certificates.length - 1 : prev - 1
    );
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) =>
      prev === certificates.length - 1 ? 0 : prev + 1
    );
  }, []);

  const openModal = (index: number) => {
    setCurrentIndex(index);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") handlePrevious();
      if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handlePrevious, handleNext]);

  return (
    <section className="w-full px-[3%] lg:px-[5%] py-12 sm:py-16">
      <div className="mx-auto max-w-[1200px] grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 items-center">
        {/* ---------- LEFT CONTENT ---------- */}
        <div className="flex flex-col justify-center text-left">
          <h3 className="text-[22px] sm:text-3xl md:text-4xl text-[#222] mb-3 sm:mb-4 leading-tight">
            Our Certifications
          </h3>
          <p className="text-[13.5px] sm:text-[15px] md:text-[16.5px] leading-[1.65] sm:leading-7 text-[#555] max-w-[580px]">
            Quality systems certified to ISO 9001:2015 and IATF 16949:2016 by
            TÜV Rheinland of North America. Certifications apply to applicable
            facilities in the United States and India.
          </p>
        </div>

        {/* ---------- RIGHT IMAGES GRID ---------- */}
        <div
          className="inline-grid grid-cols-2 gap-x-2 gap-y-3 items-start
                max-w-[380px] sm:max-w-[420px]
                md:justify-self-end"
        >
          {certificates.map((cert, index) => (
            <div key={cert.id} className="relative w-fit mx-auto group">
              {/* Clickable image only */}
              <Image
                src={cert.src}
                alt={`Certificate ${cert.id}`}
                width={170}
                height={260}
                onClick={() => openModal(index)}
                className="cursor-pointer rounded-lg border border-black shadow-[0_3px_10px_rgba(0,0,0,0.1)] object-cover transition-transform duration-300 group-hover:scale-105"
              />

              {/* Hover overlay */}
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-black/25 rounded-lg">
                <Eye className="w-5 h-5 text-white" />
                <span className="text-white text-[11px] sm:text-sm font-medium">
                  View
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---------- MODAL VIEWER ---------- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[760px] p-0 overflow-hidden">
          <div className="relative bg-white">
            <div className="relative w-full aspect-[3/4] bg-white">
              <Image
                src={certificates[currentIndex].src}
                alt={`Certificate ${certificates[currentIndex].id}`}
                fill
                className="object-contain"
                priority
              />
            </div>

            {/* Nav arrows */}
            <button
              onClick={handlePrevious}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all"
              aria-label="Previous certificate"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all"
              aria-label="Next certificate"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
